// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Modal, Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import appColors, { alpha } from '@/theme/colors';

import { AnalysisJobSwimlane } from './AnalysisJobSwimlane';
import { useAnalysisSteps } from './useAnalysisSteps';

type Translate = (key: string, params?: Record<string, string | number>) => string;

/**
 * How one analysis run was carried out, opened from its row on the fleet page.
 *
 * The row says a run succeeded and when; this says what it did. Stage names are translated
 * because there are five of them and they are the headings; the counters inside a stage are
 * printed with their own keys, because they belong to the analyser's pipeline and change with
 * it — a translated copy of each would be a second place to keep in step, in three locales,
 * for a panel only developers and admins can open.
 */
export function AnalysisStepsModal({
  apiBaseUrl,
  token,
  runId,
  onClose,
  t,
}: {
  apiBaseUrl: string;
  token: string | null;
  /** Null keeps the modal closed; the fetch starts when it is set. */
  runId: string | null;
  onClose: () => void;
  t: Translate;
}) {
  const state = useAnalysisSteps(apiBaseUrl, token, runId);

  return (
    <Modal
      visible={runId !== null}
      animationType="slide"
      transparent
      onRequestClose={onClose}
      testID="analysis-steps-modal"
    >
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <View style={styles.headerRow}>
            <Text style={styles.title}>{t('batchFleetPage.stepsTitle')}</Text>
            <Pressable
              onPress={onClose}
              accessibilityRole="button"
              testID="analysis-steps-close"
              style={styles.closeButton}
            >
              <Text style={styles.closeText}>{t('batchFleetPage.stepsClose')}</Text>
            </Pressable>
          </View>

          {state.status === 'loading' ? (
            <Text style={styles.paragraph}>{t('batchFleetPage.stepsLoading')}</Text>
          ) : state.status === 'forbidden' ? (
            <Text style={styles.paragraph} testID="analysis-steps-forbidden">
              {t('batchFleetPage.forbidden')}
            </Text>
          ) : state.status === 'failed' ? (
            <Text style={styles.paragraph} testID="analysis-steps-failed">
              {t('batchFleetPage.stepsFailed')}
            </Text>
          ) : state.status === 'empty' ? (
            <Text style={styles.paragraph} testID="analysis-steps-empty">
              {t('batchFleetPage.stepsEmpty')}
            </Text>
          ) : (
            <ScrollView style={styles.scroll}>
              <Text style={styles.runMeta} testID="analysis-steps-run">
                {[
                  state.run.courseName ?? state.run.courseId,
                  state.run.detector,
                  state.run.modelVersion,
                  state.run.durationSeconds === null
                    ? null
                    : t('batchFleetPage.stepsSeconds', { seconds: state.run.durationSeconds }),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>

              <AnalysisJobSwimlane
                jobs={state.run.jobs}
                startedAt={state.run.startedAt}
                finishedAt={state.run.finishedAt}
                t={t}
              />

              {state.run.providers.map((provider) => (
                <View key={provider.jobId ?? provider.provider} style={styles.providerCard}>
                  <Text style={styles.providerName}>
                    {[provider.provider, provider.providerVersion].filter(Boolean).join(' · ')}
                  </Text>
                  {provider.error ? (
                    <Text style={styles.errorText}>{provider.error}</Text>
                  ) : null}

                  {provider.steps.map((step, index) => (
                    <View key={`${step.name}-${index}`} style={styles.stepRow} testID="analysis-steps-step">
                      <View style={styles.stepHeader}>
                        <Text style={styles.stepName}>{t(`batchFleetPage.stepName.${step.name}`)}</Text>
                        <Text style={styles.stepSeconds}>
                          {t('batchFleetPage.stepsSeconds', { seconds: round(step.seconds) })}
                        </Text>
                      </View>
                      <View style={styles.detailWrap}>
                        {Object.entries(step.detail).map(([key, value]) => (
                          <View key={key} style={styles.detailChip}>
                            <Text style={styles.detailText}>{`${key} ${value}`}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  ))}
                </View>
              ))}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
}

/** One decimal is the resolution a person reads; the stored value keeps the rest. */
const round = (seconds: number): number => Math.round(seconds * 10) / 10;

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: alpha(appColors.neutral.black, 0.5),
    justifyContent: 'flex-end',
  },
  sheet: {
    maxHeight: '85%',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  title: { fontSize: 18, fontWeight: '700', color: appColors.text.primary },
  closeButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
  },
  closeText: { fontSize: 14, color: appColors.text.primary },
  paragraph: { fontSize: 14, color: appColors.text.secondary, paddingVertical: 12 },
  scroll: { flexGrow: 0 },
  runMeta: { fontSize: 13, color: appColors.text.secondary, marginBottom: 8 },
  providerCard: {
    borderRadius: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    padding: 12,
    marginBottom: 12,
    gap: 8,
  },
  providerName: { fontSize: 15, fontWeight: '600', color: appColors.text.primary },
  errorText: { fontSize: 13, color: appColors.status.danger },
  stepRow: { gap: 6 },
  stepHeader: { flexDirection: 'row', justifyContent: 'space-between', gap: 8 },
  stepName: { fontSize: 14, fontWeight: '600', color: appColors.text.primary },
  stepSeconds: { fontSize: 13, color: appColors.text.muted },
  detailWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  detailChip: {
    borderRadius: 999,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: appColors.border.default,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  detailText: { fontSize: 12, color: appColors.text.secondary },
});
